import { useState } from "react";
import { de } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";

// Das Leistungsdatum wird im Feld `rechnungszeitraum` als Text gespeichert:
//   einzelner Tag: "05.10.2026"   Zeitraum: "01.10.2026 – 15.10.2026"
// Ältere Rechnungen enthalten dort noch "Oktober 2026" – das parst hier zu leer.

const pad = (n) => String(n).padStart(2, "0");
const fmtDate = (d) => (d ? `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}` : "");
const sameDay = (a, b) => !!a && !!b && a.toDateString() === b.toDateString();

export const emptyLeistungsdatum = () => ({ single: false, from: null, to: null });

export function formatLeistungsdatum({ single, from, to }) {
  if (!from) return "";
  if (single || !to || sameDay(from, to)) return fmtDate(from);
  return `${fmtDate(from)} – ${fmtDate(to)}`;
}

export function parseLeistungsdatum(text) {
  const dates = [...(text || "").matchAll(/(\d{2})\.(\d{2})\.(\d{4})/g)]
    .map(([, d, m, y]) => new Date(Number(y), Number(m) - 1, Number(d)));
  if (dates.length === 0) return emptyLeistungsdatum();
  if (dates.length === 1 || sameDay(dates[0], dates[1])) return { single: true, from: dates[0], to: null };
  return { single: false, from: dates[0], to: dates[1] };
}

// Fehlermeldung oder null
export function validateLeistungsdatum({ single, from, to }) {
  if (!from) return single ? "Bitte Leistungsdatum wählen" : "Bitte Leistungsdatum „von“ wählen";
  if (!single && !to) return "Bitte Leistungsdatum „bis“ wählen oder „Einzelner Tag“ aktivieren";
  return null;
}

function DateField({ label, value, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 min-w-0 h-10 rounded-md border bg-white px-3 text-left text-sm flex items-center gap-2 transition ${
        active ? "border-[#6B7F3E] ring-2 ring-[#6B7F3E]/20" : "border-input hover:border-[#6B7F3E]/60"
      }`}
    >
      <CalendarDays className="w-4 h-4 text-[#6B7F3E] shrink-0" />
      <span className="text-[11px] uppercase tracking-wider text-[#5C6B5D] shrink-0">{label}</span>
      <span className={`truncate ${value ? "text-[#1A201A]" : "text-[#9CA39C]"}`}>{value || "TT.MM.JJJJ"}</span>
    </button>
  );
}

export default function LeistungsdatumPicker({ value, onChange, testIdPrefix = "leistungsdatum" }) {
  const { single, from, to } = value;
  const [open, setOpen] = useState(false);
  // Welches Feld der nächste Klick im Kalender setzt
  const [active, setActive] = useState("from");
  const [month, setMonth] = useState(from || new Date());

  const openField = (field) => {
    setActive(field);
    setMonth((field === "to" && to) || from || new Date());
    setOpen(true);
  };

  const setMode = (nextSingle) => {
    if (nextSingle === single) return;
    onChange({ single: nextSingle, from, to: nextSingle ? null : to });
    setOpen(false);
  };

  const onDayClick = (day) => {
    if (single) {
      onChange({ single, from: day, to: null });
      setOpen(false);
      return;
    }
    if (active === "from" || !from) {
      // Ein neues "von" nach dem bisherigen "bis" verwirft das "bis".
      onChange({ single, from: day, to: to && day <= to ? to : null });
      setActive("to");
      return;
    }
    if (day < from) {
      onChange({ single, from: day, to });
      return;
    }
    onChange({ single, from, to: day });
    setOpen(false);
  };

  return (
    <div>
      <div className="inline-flex rounded-md border border-[#E5E7EB] bg-[#F5F6F0] p-0.5 mb-2 text-xs" role="group" aria-label="Art des Leistungsdatums">
        {[[false, "Zeitraum"], [true, "Einzelner Tag"]].map(([s, label]) => (
          <button
            key={label}
            type="button"
            onClick={() => setMode(s)}
            aria-pressed={single === s}
            data-testid={`${testIdPrefix}-mode-${s ? "single" : "range"}`}
            className={`px-3 py-1 rounded transition ${
              single === s ? "bg-white text-[#2C3E2D] font-medium shadow-sm" : "text-[#5C6B5D] hover:text-[#2C3E2D]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div className="flex flex-col sm:flex-row gap-2">
            {single ? (
              <DateField
                label="Datum" value={fmtDate(from)} active={open}
                onClick={() => openField("from")} testId={`${testIdPrefix}-single`}
              />
            ) : (
              <>
                <DateField
                  label="Von" value={fmtDate(from)} active={open && active === "from"}
                  onClick={() => openField("from")} testId={`${testIdPrefix}-from`}
                />
                <DateField
                  label="Bis" value={fmtDate(to)} active={open && active === "to"}
                  onClick={() => openField(from ? "to" : "from")} testId={`${testIdPrefix}-to`}
                />
              </>
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent align="start" className="w-auto p-0 bg-white">
          <Calendar
            locale={de}
            weekStartsOn={1}
            mode={single ? "single" : "range"}
            selected={single ? from || undefined : { from: from || undefined, to: to || undefined }}
            onDayClick={onDayClick}
            month={month}
            onMonthChange={setMonth}
          />
          {!single && (
            <div className="px-3 pb-3 -mt-1 text-[11px] text-[#5C6B5D]">
              {active === "from" || !from ? "Startdatum wählen" : "Enddatum wählen"}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
